import { Modal } from '../../../components/modal/modal';
import { useEffect, useState } from 'react';
import { Button } from '../../../components/form/button';
import {
  CanOnRentValidationError,
  OnRentRequest,
  Order,
  OrderReturnStatus,
  OrderStatusTransitionPaymentRequirement,
  OrderType,
} from '../../../clients/quipli-service/order/order';
import { useOrderApiV1 } from '../../../clients/quipli-service/order/order-api-v1';
import { Spinner } from '../../../components/spinner/spinner';
import { toast } from '../../../util/toasts';
import { PaymentMethodRequestV2 } from '../../../clients/quipli-service/payment/payment-v2';
import { Toggle } from '../../../components/form/toggle';
import { Checkbox } from '../../../components/checkbox/checkbox';
import { usePaymentApiV2 } from '../../../clients/quipli-service/payment/payment-api-v2';
import { Invoice } from '../../../clients/quipli-service/invoice/invoice';
import { Payment, PaymentMethodType } from '../../../clients/quipli-service/payment/payment';
import { round } from 'lodash';
import './status-transitions.scss';
import { ValidationError } from '../../../components/validation-error/validation-error';
import classNames from 'classnames';
import { displayMoneyFormat } from '../../../util/util';
import { DateTimePicker } from '../../../components/date-picker/date-time-picker';
import { DateTime } from 'luxon';
import { ProductTrackingType, ProductType } from '../../../clients/quipli-service/product/product';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { orderToPaymentMethodV2, StatusTransitionPayment } from './status-transition-payment';

export const OnRentOrder = ({
  orderId,
  onCancel,
  onComplete,
}: {
  orderId: number;
  onCancel: () => void;
  onComplete: () => void;
}) => {
  const { onRentOrder, canOnRentOrder, getStatusTransitionPaymentRequirement, getOrderById } = useOrderApiV1();
  const { createPayment } = usePaymentApiV2();
  const { getMultipleConfigurations } = useAPI();
  const [isOnRenting, setIsOnRenting] = useState<boolean>(false);
  const [order, setOrder] = useState<Order>();
  const [invoice, setInvoice] = useState<Invoice | undefined>();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodRequestV2 | undefined>();
  const [onRentWithPayment, setOnRentWithPayment] = useState<boolean>(true);
  const [amount, setAmount] = useState<string>();
  const [statusTransitionPaymentRequirement, setStatusTransitionPaymentRequirement] =
    useState<Partial<OrderStatusTransitionPaymentRequirement>>();
  const [request, setRequest] = useState<OnRentRequest>();
  const [canOnRent, setCanOnRent] = useState<CanOnRentValidationError[]>();
  const hasSaleItems = order?.products.some((p) => p.type === OrderType.Sale) || false;
  const requiresFullPayment = hasSaleItems && !order?.isCycleBilled;
  const [unitTrackingType, setUnitTrackingType] = useState<'serialNumber' | 'unitNumber'>('unitNumber');
  const [payments, setPayments] = useState<Payment[]>();
  const [facilitate, setFacilitate] = useState<boolean>(false);
  const [seisIntegrationEnabled, setSeisIntegrationEnabled] = useState<boolean>(false);
  const [createCertificateOnApproval, setCreateCertificateOnApproval] = useState<boolean>(false);
  const [onOffRentUpdateDateConfiguration, setOnOffRentUpdateDateConfiguration] = useState<boolean>(false);
  const [onOffRentUpdateDateCheckbox, setOnOffRentUpdateDateCheckbox] = useState<boolean>(true);

  useEffect(() => {
    getOrderById(orderId)
      .then(setOrder)
      .catch((e) => toast(e.message, 'error'));
    canOnRentOrder(orderId)
      .then(setCanOnRent)
      .catch((e) => toast(e.message, 'error'));
    getStatusTransitionPaymentRequirement(OrderReturnStatus.OnRent).then((tpr) =>
      setStatusTransitionPaymentRequirement(tpr || {}),
    );
    getMultipleConfigurations([
      'CONF_UNIT_TRACKING_VIA',
      'CONF_SEIS_INTEGRATION',
      'CONF_CREATE_SEIS_CERTS_ON_RENT',
      'CONF_ON_OFF_RENT_UPDATE_ORDER_DATE',
    ]).then((configs) => {
      setUnitTrackingType(
        configs.find((c) => c.variable === 'CONF_UNIT_TRACKING_VIA')?.value === '1' ? 'serialNumber' : 'unitNumber',
      );
      setSeisIntegrationEnabled(configs.find((c) => c.variable === 'CONF_SEIS_INTEGRATION')?.value === '1');
      setCreateCertificateOnApproval(
        configs.find((c) => c.variable === 'CONF_CREATE_SEIS_CERTS_ON_RENT')?.value !== '1',
      );
      setOnOffRentUpdateDateConfiguration(
        configs.find((c) => c.variable === 'CONF_ON_OFF_RENT_UPDATE_ORDER_DATE')?.value?.includes('onrent') ?? false,
      );
    });
  }, []);

  useEffect(() => {
    if (invoice) {
      if (invoice.balance <= 0) {
        setOnRentWithPayment(false);
      }
    }
  }, [invoice]);

  useEffect(() => {
    if (order) {
      if (order.isCycleBilled || order.paymentMethod.type === PaymentMethodType.OnAccount) {
        setOnRentWithPayment(false);
      }
      const orderProducts = order.products.filter(
        (op) => op.type === OrderType.Rental && (!op.status || op.status === OrderReturnStatus.NotStarted),
      );
      if (orderProducts.length > 0) {
        setRequest({
          orderProductIds: orderProducts.map((op) => op.id),
          timestamp: onOffRentUpdateDateConfiguration
            ? DateTime.local({ zone: orderProducts[0].rentalStartDate.zone.name })
            : DateTime.min(
                ...orderProducts.map((op) => op.rentalStartDate),
                DateTime.local({ zone: orderProducts[0].rentalStartDate.zone }),
              ),
          hourReadings: orderProducts
            .flatMap((op) => op.assignedUnits)
            .map((au) => {
              return { unitId: au.id, hourReading: au.lastHourReading?.hour || 0 };
            }),
          unitLocationUpdates: [],
          onRentUpdateDates: onOffRentUpdateDateConfiguration ? onOffRentUpdateDateCheckbox : undefined,
        });
        setPaymentMethod(orderToPaymentMethodV2(order));
        setInvoice(order.invoices.find((inv) => inv));
      }
    }
  }, [order]);

  async function postPayment(payments?: Payment[]) {
    setFacilitate(false);
    if (payments) {
      if (payments[0]?.gatewayStatus === 'requires_action') {
        toast('Payment requires authentication', 'error');
      } else if (payments[0]?.gatewayStatus == 'processing') {
        toast('Bank payment is processing, once approved you may proceed with on rent');
        setIsOnRenting(false);
        return;
      } else if (payments[0]?.status === 'failed') {
        toast('Payment failed', 'error');
        setIsOnRenting(false);
        return;
      }
    }
    await onRentOrder(orderId, request!);
    onComplete();
  }

  async function doOnRent() {
    if (request) {
      try {
        setIsOnRenting(true);
        if (
          onRentWithPayment &&
          (!onOffRentUpdateDateConfiguration || !onOffRentUpdateDateCheckbox) &&
          invoice &&
          paymentMethod &&
          amount &&
          parseFloat(amount) > 0
        ) {
          const payment = await createPayment({
            invoiceIds: [invoice.id],
            paymentMethod,
            amount: parseFloat(amount),
            userId: invoice.customer.id,
          });
          if (!payment) {
            toast('Payment failed', 'error');
            return;
          }

          if (paymentMethod.type === PaymentMethodType.Stripe && paymentMethod.terminalId) {
            setFacilitate(true);
            setPayments([payment]);
            return;
          } else {
            if (payment.gatewayStatus === 'requires_action') {
              toast('Payment requires authentication', 'error');
            } else if (payment.gatewayStatus == 'processing') {
              toast('Bank payment is processing, once approved you may proceed with on rent');
            } else if (payment.status === 'failed') {
              toast('Payment failed', 'error');
              return;
            }
            await onRentOrder(orderId, request);
            onComplete();
          }
        } else {
          await onRentOrder(orderId, request);
          onComplete();
        }
      } catch (err: any) {
        toast(err.message, 'error');
      } finally {
        if (!(paymentMethod?.type === PaymentMethodType.Stripe && paymentMethod?.terminalId)) {
          setIsOnRenting(false);
        }
      }
    }
  }

  const numericAmount = amount !== undefined && parseFloat(amount);
  const requiresAdditionalPayment =
    order &&
    order.paymentMethod.type !== PaymentMethodType.OnAccount &&
    !order.isCycleBilled &&
    invoice &&
    statusTransitionPaymentRequirement?.requiredPaymentPercentage !== undefined &&
    statusTransitionPaymentRequirement.requiredPaymentPercentage >
      round(
        (100 * (invoice.total - invoice.balance + (onRentWithPayment ? numericAmount || 0 : 0))) / invoice.total,
        2,
      );
  const paymentGreaterThanBalance = numericAmount && invoice && numericAmount > invoice.balance;
  const validationErrors = canOnRent?.filter((e) => {
    if (e.scope === 'OrderProduct') {
      if (request?.orderProductIds.includes(e.orderProductId)) {
        return e.errorType !== 'UnitLocationValidationError' || !request.unitLocationUpdates.includes(e.unitId);
      } else {
        return false;
      }
    }
    return true;
  });
  const isValid =
    !requiresAdditionalPayment &&
    !paymentGreaterThanBalance &&
    validationErrors &&
    validationErrors.length === 0 &&
    request &&
    request.orderProductIds.length > 0;

  const noProductsToOnRent =
    order &&
    !order?.products.some(
      (op) => op.type === OrderType.Rental && (!op.status || op.status === OrderReturnStatus.NotStarted),
    );

  const orderValidationMessages = [];
  if (requiresAdditionalPayment) {
    orderValidationMessages.push(`At least ${statusTransitionPaymentRequirement?.requiredPaymentPercentage}% of this order must be
        paid to on rent`);
  }
  if (paymentGreaterThanBalance) {
    orderValidationMessages.push(
      `Cannot collect more than the order balance of ${displayMoneyFormat(invoice.balance, 'usd')}`,
    );
  }
  orderValidationMessages.push(...(canOnRent?.filter((cor) => cor.scope === 'Order').map((cor) => cor.message) || []));
  return (
    <Modal
      isOpen={true}
      title="On Rent Order"
      actions={
        !order || isOnRenting ? null : (
          <>
            <Button className="button button-secondary" onClick={() => onCancel()}>
              Cancel
            </Button>
            <Button className="button button-yellow" disabled={!isValid} onClick={() => doOnRent()}>
              On Rent
            </Button>
          </>
        )
      }
      close={() => onCancel()}
    >
      {noProductsToOnRent && (
        <ValidationError>
          <ul>
            <li>No remaining products to on rent</li>
          </ul>
        </ValidationError>
      )}
      {!(order && request && !noProductsToOnRent) ||
      (isOnRenting && !(paymentMethod?.type == PaymentMethodType.Stripe && paymentMethod.terminalId)) ||
      noProductsToOnRent ? (
        !noProductsToOnRent && <Spinner />
      ) : (
        <div className="flex flex-column w-full gap-2">
          <div className="flex flex-column w-full gap-2">
            <h6 className="m-0">On Rent At</h6>
            <DateTimePicker
              value={request.timestamp}
              onChange={(d) =>
                d &&
                setRequest((r) => {
                  return !r ? undefined : { ...r, timestamp: d };
                })
              }
              timezone={request.timestamp.zone.name}
            />
            {onOffRentUpdateDateConfiguration && (
              <Checkbox
                label="Update product start with on-rent date/time"
                isChecked={onOffRentUpdateDateCheckbox}
                onChange={(value) => {
                  setOnOffRentUpdateDateCheckbox(value);
                  setRequest((r) => {
                    return !r ? undefined : { ...r, onRentUpdateDates: value };
                  });
                }}
              />
            )}
          </div>
          {seisIntegrationEnabled && !createCertificateOnApproval && (
            <>
              {request.timestamp.diffNow().as('hours') < -1 && (
                <ValidationError className="warning">
                  <ul>
                    <li>
                      Rental damage waiver insurance cannot be created automatically for on rent time more than an hour
                      in the past. You must contact SEIS directly to add the RDW insurance for these products going on
                      rent.
                    </li>
                  </ul>
                </ValidationError>
              )}
              {(!order.user.phone || !order.user.email || !order.billingAddress) && (
                <ValidationError>
                  <ul>
                    <li>
                      Customer must have a phone number, email address, and billing address to create the rental damage
                      waiver insurance.
                    </li>
                  </ul>
                </ValidationError>
              )}
              {!order.invoices.length && (
                <ValidationError>
                  <ul>
                    <li>Order must have an invoice to create the rental damage waiver insurance.</li>
                  </ul>
                </ValidationError>
              )}
            </>
          )}
          <div className="flex flex-column w-full gap-2">
            <h6 className="m-0">Products</h6>
            <ul className="list-none p-0 m-0">
              {order.products
                .filter(
                  (op) => op.type === OrderType.Rental && (!op.status || op.status === OrderReturnStatus.NotStarted),
                )
                .map((op) => {
                  const isSelected = request.orderProductIds.indexOf(op.id) >= 0;
                  const onToggle = isSelected
                    ? () =>
                        setRequest((r) => {
                          return !r
                            ? undefined
                            : { ...r, orderProductIds: r.orderProductIds.filter((opId) => opId !== op.id) };
                        })
                    : () =>
                        setRequest((r) => {
                          return !r
                            ? undefined
                            : { ...r, orderProductIds: [...r.orderProductIds.filter((opId) => opId !== op.id), op.id] };
                        });
                  const validationErrors =
                    canOnRent?.filter((cor) => cor.scope === 'OrderProduct' && cor.orderProductId === op.id) || [];
                  const hasLocationValidationError = validationErrors.some(
                    (e) => e.errorType === 'UnitLocationValidationError',
                  );
                  return (
                    <li key={op.id}>
                      <div className="flex gap-2 items-center">
                        <div>
                          <Toggle checked={isSelected} onClick={onToggle} />
                        </div>
                        <div>{op.product.type === ProductType.Custom ? op.name : op.product.name}</div>
                      </div>
                      {isSelected && (
                        <div className="ml-8 my-4 flex gap-2 flex-column">
                          {op.product.trackingType === ProductTrackingType.SerialUnits && (
                            <ul className="list-none p-0">
                              <li className="flex gap-2 items-center p-1">
                                <div className="basis-1/3 text-center">Unit</div>
                                {op.product.allowHourTracking && (
                                  <div className="basis-1/3 text-center">Hour Reading</div>
                                )}
                                {hasLocationValidationError && (
                                  <div className="basis-1/3 text-center">Re-assign Location</div>
                                )}
                              </li>
                              {op.assignedUnits.map((au, i) => (
                                <li key={i} className="flex gap-2 items-center p-1">
                                  <label className="basis-1/3 text-center">{au[unitTrackingType]}</label>
                                  {op.product.allowHourTracking && (
                                    <div className="basis-1/3 text-center">
                                      <input
                                        className="w-full"
                                        type="number"
                                        min={0}
                                        value={
                                          request.hourReadings.find((hr) => hr.unitId === au.id)?.hourReading || ''
                                        }
                                        onChange={(e) =>
                                          e.target.value &&
                                          setRequest((r) => {
                                            return r
                                              ? {
                                                  ...r,
                                                  hourReadings: [
                                                    ...r.hourReadings.filter((hr) => hr.unitId !== au.id),
                                                    { unitId: au.id, hourReading: parseFloat(e.target.value) },
                                                  ],
                                                }
                                              : undefined;
                                          })
                                        }
                                      />
                                    </div>
                                  )}

                                  {validationErrors.some(
                                    (ve) => ve.errorType === 'UnitLocationValidationError' && ve.unitId === au.id,
                                  ) && (
                                    <div className="basis-1/3 text-center">
                                      <input
                                        type="checkbox"
                                        checked={request.unitLocationUpdates.includes(au.id)}
                                        onChange={() =>
                                          setRequest((r) => {
                                            return r
                                              ? {
                                                  ...r,
                                                  unitLocationUpdates: r.unitLocationUpdates.includes(au.id)
                                                    ? r.unitLocationUpdates.filter((uId) => uId !== au.id)
                                                    : [...r.unitLocationUpdates, au.id],
                                                }
                                              : undefined;
                                          })
                                        }
                                      />
                                    </div>
                                  )}

                                  {/* SEIS integration unit validation/warnings */}
                                  {seisIntegrationEnabled && !createCertificateOnApproval && op.product.seisRdw && (
                                    <>
                                      {!au.serialNumber && !au.unitNumber && (
                                        <ValidationError className="warning">
                                          <ul>
                                            <li>
                                              Serial number or unit number is required to create RDW insurance for this
                                              unit.
                                            </li>
                                          </ul>
                                        </ValidationError>
                                      )}
                                      {!au.classification && (
                                        <ValidationError className="warning">
                                          <ul>
                                            <li>
                                              Year, make, and model is required to create RDW insurance for this unit.
                                            </li>
                                          </ul>
                                        </ValidationError>
                                      )}

                                      {!order.isCycleBilled && !op.rentalEndDate && (
                                        <ValidationError className="warning">
                                          <ul>
                                            <li>Rental end date is required to create RDW insurance for this unit.</li>
                                          </ul>
                                        </ValidationError>
                                      )}
                                    </>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                          <ValidationError
                            className={classNames({
                              hidden:
                                !isSelected ||
                                validationErrors.filter(
                                  (e) =>
                                    e.errorType !== 'UnitLocationValidationError' ||
                                    !request.unitLocationUpdates.includes(e.unitId),
                                ).length === 0,
                            })}
                            style={{ maxWidth: '342px' }}
                          >
                            <ul>
                              {validationErrors
                                .filter(
                                  (e) =>
                                    e.errorType !== 'UnitLocationValidationError' ||
                                    !request.unitLocationUpdates.includes(e.unitId),
                                )
                                .map((e, i) => (
                                  <li key={i}>{e.message}</li>
                                ))}
                            </ul>
                          </ValidationError>
                        </div>
                      )}
                    </li>
                  );
                })}
            </ul>
          </div>
          {!onOffRentUpdateDateConfiguration || !onOffRentUpdateDateCheckbox ? (
            invoice && invoice.balance > 0 ? (
              <div className="flex flex-column gap-2">
                <h6 className="m-0">Payment</h6>
                <div className="flex gap-2 text-[0.9rem]">
                  <Toggle
                    onClick={() => {
                      setOnRentWithPayment((prev) => !prev);
                    }}
                    checked={onRentWithPayment}
                    disabled={requiresFullPayment}
                  />
                  On Rent with Payment
                </div>
                {onRentWithPayment && (
                  <StatusTransitionPayment
                    order={order}
                    paymentMethod={paymentMethod}
                    setPaymentMethod={setPaymentMethod}
                    total={invoice.total}
                    paymentTotal={invoice.total - invoice.balance}
                    amount={amount}
                    setAmount={setAmount}
                    defaultPercentage={
                      requiresFullPayment ? 100 : statusTransitionPaymentRequirement?.defaultPaymentPercentage || 0
                    }
                    payments={payments}
                    setPayments={setPayments}
                    onComplete={postPayment}
                    facilitate={facilitate}
                  />
                )}
              </div>
            ) : (
              <div>Order has been fully paid</div>
            )
          ) : (
            <></>
          )}
          <ValidationError
            className={classNames({ hidden: orderValidationMessages.length === 0 })}
            style={{ maxWidth: '342px' }}
          >
            <ul>
              {orderValidationMessages.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
          </ValidationError>
        </div>
      )}
    </Modal>
  );
};
